import type { AlignedCharacter, BookLanguage } from "@japa-reader/contracts";

export type ReaderSentence = {
  id: number;
  characters: AlignedCharacter[];
  globalStart: number;
  globalEnd: number;
  start: number;
  end: number;
};

export function buildSentences(characters: AlignedCharacter[], language: BookLanguage): ReaderSentence[] {
  const result: ReaderSentence[] = [];
  const source = characters.map((character) => character.text).join("");
  const locale = language === "ja" ? "ja-JP" : language === "ru" ? "ru-RU" : "en-US";
  const boundaries = source ? [...new Intl.Segmenter(locale, { granularity: "sentence" }).segment(source)]
    .filter((segment) => !continuesAfterAbbreviation(segment.segment, language))
    .map((segment) => segment.index + segment.segment.length) : [];
  let boundaryIndex = 0;
  let sourceOffset = 0;
  let segmentStart = 0;

  const commit = (segmentEnd: number) => {
    const segment = characters.slice(segmentStart, segmentEnd);
    if (segment.some((character) => character.text.trim())) {
      result.push({
        id: result.length,
        characters: segment,
        globalStart: segmentStart,
        globalEnd: segmentEnd,
        start: segment[0]?.start ?? 0,
        end: segment.at(-1)?.end ?? segment[0]?.end ?? 0,
      });
    }
    segmentStart = segmentEnd;
  };

  characters.forEach((character, index) => {
    sourceOffset += character.text.length;
    const reachedBoundary = boundaryIndex < boundaries.length && sourceOffset >= boundaries[boundaryIndex];
    if (reachedBoundary || character.text.includes("\n")) {
      commit(index + 1);
      while (boundaryIndex < boundaries.length && sourceOffset >= boundaries[boundaryIndex]) boundaryIndex += 1;
    }
  });
  if (segmentStart < characters.length) commit(characters.length);
  return result;
}

function continuesAfterAbbreviation(segment: string, language: BookLanguage) {
  const value = segment.trim();
  if (language === "en") {
    return /(?:\b(?:mr|mrs|ms|dr|prof|sr|jr|st|mt|vs|etc|e\.g|i\.e)|(?:^|\s)[a-z])\.$/iu.test(value);
  }
  if (language === "ru") {
    return /(?:\b(?:г|ул|д|т|им|тов|рис|стр)|(?:^|\s)[а-яё])\.$/iu.test(value);
  }
  return false;
}
